import React, { useState, useEffect } from "react";
import api from "../services/api";

const STATUS_COLORS = {
  Open: { bg: "#fee2e2", text: "#991b1b" },
  "In Progress": { bg: "#fef3c7", text: "#92400e" },
  Resolved: { bg: "#dcfce7", text: "#166534" },
};

function TicketsDashboard() {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyStatus, setReplyStatus] = useState("");
  const [sending, setSending] = useState(false);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const res = await api.get("/tickets", {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const openTicket = async (ticketId) => {
    try {
      const res = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(res.data.ticket);
      setReplyStatus(res.data.ticket.status);
    } catch (err) {
      alert("Failed to load ticket");
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSending(true);
      const res = await api.post(`/tickets/${selectedTicket._id}/reply`, {
        message: replyText.trim(),
        status: replyStatus,
      });
      setSelectedTicket(res.data.ticket);
      setReplyText("");
      loadTickets();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleQuickResolve = async () => {
    if (!window.confirm("Mark this ticket as Resolved?")) return;

    try {
      const res = await api.post(`/tickets/${selectedTicket._id}/status`, {
        status: "Resolved",
      });
      setSelectedTicket(res.data.ticket);
      setReplyStatus("Resolved");
      loadTickets();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Support Tickets</h1>

      <div style={styles.filterRow}>
        {["", "Open", "In Progress", "Resolved"].map((s) => (
          <button
            key={s || "all"}
            style={{
              ...styles.filterBtn,
              ...(statusFilter === s ? styles.filterBtnActive : {}),
            }}
            onClick={() => setStatusFilter(s)}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.emptyState}>Loading...</div>
      ) : tickets.length === 0 ? (
        <div style={styles.emptyState}>No tickets found.</div>
      ) : (
        <div style={styles.list}>
          {tickets.map((t) => (
            <div key={t._id} style={styles.ticketRow} onClick={() => openTicket(t._id)}>
              <div style={{ flex: 1 }}>
                <div style={styles.ticketTopRow}>
                  <span style={styles.raisedBy}>
                    {t.raisedByModel === "User" ? "👤" : "🚚"} {t.raisedBy?.name || "Unknown"}{" "}
                    ({t.raisedBy?.phone || "—"})
                  </span>
                  {t.booking && <span style={styles.bookingTag}>Booking #{t.booking._id.slice(-6)}</span>}
                  {t.hasAdminUnread && <span style={styles.unreadDot} />}
                </div>
                <div style={styles.lastMessage}>
                  {t.messages?.[t.messages.length - 1]?.text || ""}
                </div>
              </div>
              <span
                style={{
                  ...styles.statusBadge,
                  background: STATUS_COLORS[t.status]?.bg,
                  color: STATUS_COLORS[t.status]?.text,
                }}
              >
                {t.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {selectedTicket && (
        <div style={styles.modalOverlay} onClick={() => setSelectedTicket(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>
                {selectedTicket.raisedByModel === "User" ? "👤" : "🚚"}{" "}
                {selectedTicket.raisedBy?.name} ({selectedTicket.raisedBy?.phone})
              </h3>
              <button style={styles.closeBtn} onClick={() => setSelectedTicket(null)}>✕</button>
            </div>

            {selectedTicket.booking && (
              <div style={styles.bookingInfo}>
                Booking #{selectedTicket.booking._id.slice(-6)} — {selectedTicket.booking.status}
                <br />
                {selectedTicket.booking.pickupLocation} → {selectedTicket.booking.dropLocation}
              </div>
            )}

            <div style={styles.thread}>
              {selectedTicket.messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.messageBubble,
                    alignSelf: m.senderModel === "Admin" ? "flex-end" : "flex-start",
                    background: m.senderModel === "Admin" ? "#6366f1" : "#f1f5f9",
                    color: m.senderModel === "Admin" ? "#fff" : "#1e293b",
                  }}
                >
                  <div style={styles.msgSender}>{m.senderModel}</div>
                  <div>{m.text}</div>
                </div>
              ))}
            </div>

            <div style={styles.replySection}>
              <select
                style={styles.statusSelect}
                value={replyStatus}
                onChange={(e) => setReplyStatus(e.target.value)}
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
              </select>

              <textarea
                style={styles.replyInput}
                placeholder="Type your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={3}
              />

              <div style={{ display: "flex", gap: "10px" }}>
                <button style={styles.sendBtn} onClick={handleReply} disabled={sending || !replyText.trim()}>
                  {sending ? "Sending..." : "Send Reply"}
                </button>
                <button style={styles.resolveBtn} onClick={handleQuickResolve}>
                  Mark Resolved
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "30px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  title: { margin: "0 0 20px 0", fontSize: "26px", fontWeight: "800", color: "#0f172a" },
  filterRow: { display: "flex", gap: "8px", marginBottom: "16px" },
  filterBtn: {
    padding: "8px 16px", borderRadius: "8px", border: "1px solid #e2e8f0",
    background: "#fff", cursor: "pointer", fontWeight: "600", fontSize: "13px", color: "#475569",
  },
  filterBtnActive: { background: "#6366f1", color: "#fff", border: "1px solid #6366f1" },
  emptyState: { padding: "40px", textAlign: "center", color: "#94a3b8" },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  ticketRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#fff", padding: "16px", borderRadius: "12px", cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  ticketTopRow: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" },
  raisedBy: { fontWeight: "700", fontSize: "14px", color: "#0f172a" },
  bookingTag: { fontSize: "11px", background: "#eef2ff", color: "#4338ca", padding: "2px 8px", borderRadius: "999px", fontWeight: "700" },
  unreadDot: { width: "8px", height: "8px", borderRadius: "50%", background: "#ef4444" },
  lastMessage: { fontSize: "13px", color: "#64748b", maxWidth: "500px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  statusBadge: { padding: "5px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: "700" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modalContent: { background: "#fff", borderRadius: "16px", padding: "24px", width: "90%", maxWidth: "560px", maxHeight: "85vh", display: "flex", flexDirection: "column" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  closeBtn: { background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#94a3b8" },
  bookingInfo: { fontSize: "12px", color: "#64748b", background: "#f8fafc", padding: "10px", borderRadius: "8px", marginBottom: "12px" },
  thread: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px", maxHeight: "320px" },
  messageBubble: { maxWidth: "80%", padding: "10px 14px", borderRadius: "12px", fontSize: "13px" },
  msgSender: { fontSize: "10px", fontWeight: "800", opacity: 0.7, marginBottom: "2px", textTransform: "uppercase" },
  replySection: { borderTop: "1px solid #e2e8f0", paddingTop: "14px" },
  statusSelect: { width: "100%", padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "8px", fontSize: "13px" },
  replyInput: { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", marginBottom: "10px", boxSizing: "border-box", resize: "vertical" },
  sendBtn: { flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: "#6366f1", color: "#fff", fontWeight: "700", cursor: "pointer" },
  resolveBtn: { flex: 1, padding: "10px", borderRadius: "8px", border: "none", background: "#dcfce7", color: "#166534", fontWeight: "700", cursor: "pointer" },
};

export default TicketsDashboard;